import React from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { OHIO_INFO, OHIO_CITIES, CASE_TYPES } from "../data/content";
import { CASE_TYPE_TO_SLUG } from "../data/accidents";
import { ArrowRight, MapPin, Scale, ShieldCheck, Clock } from "lucide-react";

const SITE_URL = "https://instabridgefunding.com";

export default function StateDetail() {
  // GSC-optimized title + meta.
  // - Title stacks the two dominant query variants ("pre-settlement funding" 68 imp @ pos 9 +
  //   "lawsuit loans" 24 imp) so the SERP snippet earns clicks from BOTH searchers.
  // - Meta leads with the specific need + exact amount + speed + non-recourse guarantee, which
  //   are the three highest-converting CTR hooks in this industry.
  // - Suffix disabled: full 60-char title is engineered end-to-end for the SERP.
  useSEO(
    "Ohio Pre-Settlement Funding & Lawsuit Loans | $500–$25k in 24 Hours",
    "Ohio plaintiffs: get $500–$25,000 pre-settlement funding on your injury lawsuit — funded in 24 hours. Non-recourse (owe nothing if you lose), no credit check, attorney-founded. Cleveland, Columbus, Cincinnati, Toledo, Dayton.",
    {
      canonical: `${SITE_URL}/states/ohio`,
      suffix: false,
      jsonLd: {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Service",
            name: "Pre-Settlement Funding in Ohio",
            provider: { "@type": "Organization", name: "Instabridge Funding" },
            areaServed: {
              "@type": "State",
              name: "Ohio",
              containsPlace: OHIO_CITIES.map((c) => ({ "@type": "City", name: `${c.name}, Ohio` })),
            },
            description:
              "Non-recourse pre-settlement funding, lawsuit loans, and settlement advances for Ohio plaintiffs. $500–$25,000, funded in 24 hours. No credit check.",
            offers: {
              "@type": "Offer",
              priceCurrency: "USD",
              price: "0",
              description: "No cost if you lose the case. Non-recourse.",
            },
          },
          {
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: SITE_URL },
              { "@type": "ListItem", position: 2, name: "States", item: `${SITE_URL}/states` },
              { "@type": "ListItem", position: 3, name: "Ohio", item: `${SITE_URL}/states/ohio` },
            ],
          },
        ],
      },
    },
  );

  return (
    <div className="min-h-screen bg-white" data-testid="state-page-ohio">
      <Navbar />

      {/* Breadcrumb */}
      <div className="border-b border-[#E5E7EB] py-3">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 text-xs text-[#6B7280]">
          <Link to="/" className="hover:text-[#0A2540]">Home</Link> <span className="mx-2">/</span>
          <Link to="/states" className="hover:text-[#0A2540]">States</Link> <span className="mx-2">/</span>
          <span className="text-[#0A2540]">Ohio</span>
        </div>
      </div>

      {/* Hero — GSC-tuned H1 stacks the two dominant query variants without keyword-stuffing */}
      <section className="bg-[#F9FAFB] py-16 lg:py-20 border-b border-[#E5E7EB]">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Ohio · Pre-settlement funding</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[#0A2540] font-light leading-[1.05]">
            <span className="font-semibold">Ohio pre-settlement funding</span><br />&amp; lawsuit loans.
          </h1>
          <p className="mt-5 text-lg text-[#374151] leading-relaxed max-w-3xl font-medium">
            $500–$25,000 against your injury settlement. Funded in 24 hours. Non-recourse — repay only if you win.
          </p>
          <p className="mt-4 text-base text-[#6B7280] leading-relaxed max-w-3xl">{OHIO_INFO.intro}</p>
          <div className="flex flex-wrap gap-3 mt-9">
            <Link to="/apply"><Button className="btn-primary-navy rounded-full h-12 px-6" data-testid="ohio-apply-btn">Apply for Ohio funding <ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
            <Link to="/how-it-works"><Button variant="outline" className="rounded-full h-12 px-6">How it works</Button></Link>
          </div>
        </div>
      </section>

      {/* Stats / value */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid sm:grid-cols-3 gap-5">
          <Card icon={Clock} t="Same-day approval" d="Most Ohio cases are reviewed and approved within hours of attorney verification." />
          <Card icon={Scale} t="Settlement-first underwriting" d="We cap advances based on Ohio settlement averages so your client never overdraws their case." />
          <Card icon={ShieldCheck} t="Non-recourse" d="Lose your Ohio case? You owe nothing. We carry the risk." />
        </div>
      </section>

      {/* Cities grid */}
      <section className="py-20 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Ohio cities</div>
          <h2 className="font-display text-3xl lg:text-4xl text-[#0A2540] font-light tracking-tight">Funding in <span className="font-semibold">every major metro.</span></h2>
          <p className="mt-4 text-[#6B7280] max-w-2xl">Local pages with court info, common case types, and city-specific application paths.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 mt-10">
            {OHIO_CITIES.map((c) => (
              <Link key={c.slug} to={`/states/ohio/${c.slug}`} className="bg-white border border-[#E5E7EB] rounded-2xl p-7 hover:-translate-y-0.5 hover:shadow-md transition-all group" data-testid={`city-card-${c.slug}`}>
                <div className="flex items-center gap-2 text-[#0066FF] uppercase-label !text-[#0066FF]"><MapPin className="w-3.5 h-3.5" /> Ohio</div>
                <h3 className="font-display text-2xl lg:text-3xl font-semibold text-[#0A2540] mt-2">{c.name}</h3>
                <div className="text-xs text-[#6B7280] mt-1">Population {c.population}</div>
                <p className="text-sm text-[#374151] mt-4 leading-relaxed line-clamp-3">{c.intro}</p>
                <div className="mt-5 inline-flex items-center gap-1 text-[#0066FF] text-sm font-medium">
                  {c.name} funding <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Laws */}
      <section className="py-20">
        <div className="mx-auto max-w-5xl px-6 lg:px-10">
          <div className="uppercase-label mb-5">Ohio law & regulations</div>
          <h2 className="font-display text-3xl text-[#0A2540] font-light tracking-tight">How pre-settlement funding works in Ohio.</h2>
          <p className="mt-6 text-[#374151] leading-relaxed text-lg max-w-3xl">{OHIO_INFO.laws}</p>
        </div>
      </section>

      {/* Case types */}
      <section className="py-16 bg-[#F9FAFB]">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <h2 className="font-display text-2xl lg:text-3xl text-[#0A2540] font-semibold">Cases we fund in Ohio</h2>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CASE_TYPES.map((c) => {
              const slug = CASE_TYPE_TO_SLUG[c];
              const commonClass = "bg-white border border-[#E5E7EB] rounded-lg px-4 py-3 text-sm text-[#374151]";
              if (slug) {
                return (
                  <Link key={c} to={`/accidents/${slug}`}
                        className={`${commonClass} hover:border-[#0066FF] hover:text-[#0066FF] transition-colors flex items-center justify-between`}
                        data-testid={`ohio-case-link-${slug}`}>
                    <span>{c}</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                );
              }
              return (
                <div key={c} className={commonClass}>{c}</div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-[#0A2540] text-white">
        <div className="mx-auto max-w-3xl px-6 lg:px-10 text-center">
          <h2 className="font-display text-3xl lg:text-4xl font-semibold">Ohio plaintiff? Apply now.</h2>
          <p className="mt-3 text-white/70">Approval in hours. Funded in 24–48 hours.</p>
          <Link to="/apply" className="inline-block mt-8">
            <Button className="bg-white text-[#0A2540] hover:bg-[#0066FF] hover:text-white rounded-full h-12 px-7">Start application</Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}

const Card = ({ icon: Icon, t, d }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-xl p-6">
    <Icon className="w-5 h-5 text-[#0066FF]" />
    <h3 className="font-display text-lg font-semibold text-[#0A2540] mt-3">{t}</h3>
    <p className="text-sm text-[#6B7280] mt-2 leading-relaxed">{d}</p>
  </div>
);
